import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useSettings } from '@/context/SettingsContext';
import { PosReceipt } from '@/components/PosReceipt';
import { buildReceiptXml, epsonPrint, usesEpsonApp, noAppToastArgs } from '@/lib/eposPrint';

const payLabelUi = (m) => ({ cash: 'Cash', card: 'Card', link: 'Payment link', store_credit: 'Store credit' }[m] || m);
import { ReceiptText, Printer, Search, ExternalLink, RefreshCw } from 'lucide-react';
import { DateInput } from '@/components/ui/date-input';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const errMsg = (e, f = 'Failed') => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x?.msg || 'Invalid input').join(', ');
  return typeof d === 'string' ? d : f;
};

const PAGE = 50;

export default function POSOrdersPage() {
  const { formatCurrency, currency } = useSettings();
  const navigate = useNavigate();
  const [sales, setSales] = useState([]);
  const [total, setTotal] = useState(0);
  const [stores, setStores] = useState([]);
  const [q, setQ] = useState('');
  const [storeId, setStoreId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [syncing, setSyncing] = useState(null);

  const retrySync = async (o) => {
    setSyncing(o.id);
    try {
      const r = await axios.post(`${API}/pos/sales/${o.id}/sync-shopify`, {}, auth());
      if (r.data.synced) {
        toast.success(`Synced to Shopify${r.data.order_name ? ` as ${r.data.order_name}` : ''}`);
        load(0);
      } else {
        toast.error(r.data.error || 'Shopify sync failed', { duration: 8000 });
      }
    } catch (e) { toast.error(errMsg(e, 'Shopify sync failed')); }
    setSyncing(null);
  };

  const load = useCallback(async (skip = 0) => {
    setLoading(true);
    try {
      const params = { limit: PAGE, skip };
      if (q.trim()) params.q = q.trim();
      if (storeId) params.store_id = storeId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await axios.get(`${API}/pos/sales`, { ...auth(), params });
      setSales(skip ? (prev) => [...prev, ...(r.data.sales || [])] : (r.data.sales || []));
      setTotal(r.data.total || 0);
    } catch (e) { toast.error(errMsg(e, 'Failed to load POS orders')); }
    setLoading(false);
  }, [q, storeId, dateFrom, dateTo]);

  useEffect(() => { load(0); }, [load]);
  useEffect(() => {
    axios.get(`${API}/pos/stores`, auth()).then((r) => setStores(r.data.stores || [])).catch(() => {});
  }, []);

  const printOrder = (o) => {
    // Epson app must open synchronously inside the tap (iOS drops the
    // gesture after any await → "address is invalid"), so build locally.
    const store = stores.find((s) => s.id === o.pos?.store_id);
    if (usesEpsonApp(store)) {
      epsonPrint(buildReceiptXml({ order: o, store, currency }), () => toast.error(...noAppToastArgs()));
      return;
    }
    axios.get(`${API}/pos/sales/${o.id}/receipt`, auth())
      .then((r) => {
        if (usesEpsonApp(r.data.store)) { epsonPrint(buildReceiptXml({ ...r.data, currency }), () => toast.error(...noAppToastArgs())); return; }
        setReceipt({ ...r.data, currency });
        setTimeout(() => window.print(), 200);
      })
      .catch((e) => toast.error(errMsg(e, 'Failed to load receipt')));
  };

  const payBadge = (s) => s === 'paid'
    ? <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/40">Paid</Badge>
    : <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/40">Pending</Badge>;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4" data-testid="pos-orders-page">
      <PosReceipt receipt={receipt} />
      <div className="flex items-center gap-3">
        <ReceiptText className="w-6 h-6 text-wt-gold" />
        <div>
          <h1 className="text-2xl font-bold text-wt-text-primary">POS Orders</h1>
          <p className="text-wt-text-muted text-sm">{total} sales — every POS sale is a real order (also visible in Orders) and syncs to Shopify when connected</p>
        </div>
      </div>

      <div className="bg-wt-paper border border-wt-border rounded-xl p-3 flex flex-wrap gap-2 items-center" data-testid="pos-orders-filters">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-2.5 top-3 text-wt-text-muted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search receipt no, Shopify no, customer, phone…" className="pl-8" data-testid="pos-orders-search" />
        </div>
        <select value={storeId} onChange={(e) => setStoreId(e.target.value)}
          className="bg-wt-bg border border-wt-border rounded px-2 py-2 text-sm text-wt-text-primary h-10" data-testid="pos-orders-store-filter">
          <option value="">All stores</option>
          {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <DateInput  value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} max={dateTo || undefined} className="w-40" data-testid="pos-orders-date-from" />
        <DateInput  value={dateTo} onChange={(e) => setDateTo(e.target.value)} min={dateFrom || undefined} className="w-40" data-testid="pos-orders-date-to" />
      </div>

      <div className="bg-wt-paper border border-wt-border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-wt-text-muted border-b border-wt-border">
              <th className="p-3">Receipt</th><th className="p-3">Shopify</th><th className="p-3">Date</th>
              <th className="p-3">Store / Staff</th><th className="p-3">Customer</th><th className="p-3">Type</th>
              <th className="p-3 text-right">Total</th><th className="p-3">Payment</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {sales.map((o) => (
              <tr key={o.id} className="border-b border-wt-border/40 hover:bg-wt-bg/40" data-testid={`pos-order-row-${o.order_number}`}>
                <td className="p-3 font-mono text-wt-text-primary">{o.order_number}</td>
                <td className="p-3">
                  {o.shopify_order_name
                    ? <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30">{o.shopify_order_name}</Badge>
                    : (
                      <span className="flex items-center gap-1">
                        <span className="text-xs text-wt-text-muted" title={o.shopify_sync_error || ''}>Not synced</span>
                        <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] text-amber-400"
                          disabled={syncing === o.id} onClick={() => retrySync(o)}
                          title={o.shopify_sync_error ? `Last error: ${o.shopify_sync_error}` : 'Retry Shopify sync'}
                          data-testid={`sync-${o.order_number}`}>
                          <RefreshCw className={`w-3 h-3 mr-0.5 ${syncing === o.id ? 'animate-spin' : ''}`} />Sync
                        </Button>
                      </span>
                    )}
                  {o.shopify_sync_error && !o.shopify_order_name && (
                    <div className="text-[10px] text-red-400/80 max-w-[180px] truncate" title={o.shopify_sync_error}>{o.shopify_sync_error}</div>
                  )}
                </td>
                <td className="p-3 text-wt-text-muted whitespace-nowrap">{(o.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                <td className="p-3 text-wt-text-muted">{o.pos?.store_name}<div className="text-xs">{o.pos?.staff_name}</div></td>
                <td className="p-3 text-wt-text-primary">{o.customer_name}{o.customer_phone && <div className="text-xs text-wt-text-muted">{o.customer_phone}</div>}</td>
                <td className="p-3 text-xs text-wt-text-muted">{o.pos?.fulfillment === 'delivery' ? 'Delivery' : 'Taken now'}{o.pos?.order_type && o.pos.order_type !== 'retail' ? ` · ${o.pos.order_type.replace('_', '-')}` : ''}</td>
                <td className="p-3 text-right font-medium text-wt-text-primary">{formatCurrency(o.total_amount)}</td>
                <td className="p-3">
                  {payBadge(o.payment_status)}
                  {(o.pos?.payments || []).length > 0 && (
                    <div className="text-[10px] text-wt-text-muted mt-0.5 max-w-[160px]" data-testid={`payments-${o.order_number}`}>
                      {(o.pos.payments).map((p) => `${p.label || payLabelUi(p.method)} ${Number(p.amount).toFixed(2)}${p.reference ? ` #${p.reference}` : ''}`).join(' + ')}
                    </div>
                  )}
                </td>
                <td className="p-3 whitespace-nowrap">
                  <Button size="sm" variant="outline" className="border-wt-border h-8" onClick={() => printOrder(o)} data-testid={`print-${o.order_number}`}>
                    <Printer className="w-3.5 h-3.5 mr-1" />Print
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 ml-1 text-wt-text-muted" onClick={() => navigate(`/orders?openOrder=${o.id}`)} data-testid={`view-${o.order_number}`}>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
            {sales.length === 0 && !loading && (
              <tr><td colSpan={9} className="p-8 text-center text-wt-text-muted">No POS sales yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {sales.length < total && (
        <div className="text-center">
          <Button variant="outline" className="border-wt-border" disabled={loading} onClick={() => load(sales.length)} data-testid="pos-orders-load-more">
            {loading ? 'Loading…' : `Load more (${sales.length} of ${total})`}
          </Button>
        </div>
      )}
    </div>
  );
}
